import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import { Observable, BehaviorSubject, from, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { switchMap, finalize, catchError } from 'rxjs/operators';

export interface VesSwapReportItem {
  userId: string;
  userName: string;
  userEmail: string;
  userCountry: string;
  userNationality: string;
  userIdType: string;
  userIdNumber: string;
  isCompany: boolean;
  companyName: string | null;
  companyRut: string | null;
  usdtAmount: number;
  vesRate: number;
  vesTotal: number;
  transactionId: string;
  swapDate: string;
}

export interface VesSwapReportSummary {
  totalSwaps: number;
  totalUsdtSwapped: number;
  totalVesGenerated: number;
  averageRate: number;
}

export interface VesSwapReportPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface VesSwapReportFilters {
  userId?: string;
  startDate?: string;
  endDate?: string;
}

export interface VesSwapReportResponse {
  swaps: VesSwapReportItem[];
  summary: VesSwapReportSummary;
  pagination: VesSwapReportPagination;
  filters: VesSwapReportFilters;
}

export interface VesSwapReportQueryParams {
  page?: number;
  limit?: number;
  userId?: string;
  startDate?: string;
  endDate?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ConversionsVesReportService {
  private apiUrl = environment.apiNest;
  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  constructor(
    private http: HttpClient,
    private cognitoService: CognitoService,
  ) {}

  private async getHeaders(): Promise<HttpHeaders> {
    const session = await this.cognitoService.getSession();
    if (!session || !session.accessToken || !session.accessToken.jwtToken) {
      throw new Error('No se pudo obtener el token de sesión');
    }

    const token = session.accessToken.jwtToken.trim();
    return new HttpHeaders().set('Authorization', `Bearer ${token}`);
  }

  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'Ha ocurrido un error en el servicio';

    if (error.error instanceof ErrorEvent) {
      errorMessage = `Error: ${error.error.message}`;
    } else {
      switch (error.status) {
        case 401:
          errorMessage =
            'Error de autenticación. Por favor, inicie sesión nuevamente.';
          break;
        case 403:
          errorMessage = 'No tiene permisos para realizar esta acción.';
          break;
        case 404:
          errorMessage = 'El servicio no está disponible en este momento.';
          break;
        case 500:
          errorMessage =
            'Error interno del servidor. Por favor, intente más tarde.';
          break;
        default:
          errorMessage = error.error?.message || errorMessage;
      }
    }

    return throwError(() => ({ message: errorMessage, originalError: error }));
  }

  getVesSwapsReport(
    params: VesSwapReportQueryParams = {},
  ): Observable<VesSwapReportResponse> {
    this.loadingSubject.next(true);

    let httpParams = new HttpParams();
    if (params.page) httpParams = httpParams.set('page', params.page.toString());
    if (params.limit)
      httpParams = httpParams.set('limit', params.limit.toString());
    if (params.userId) httpParams = httpParams.set('userId', params.userId);
    if (params.startDate)
      httpParams = httpParams.set('startDate', params.startDate);
    if (params.endDate) httpParams = httpParams.set('endDate', params.endDate);

    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        return this.http.get<VesSwapReportResponse>(
          `${this.apiUrl}binance-deposits/ves-swaps`,
          { headers, params: httpParams },
        );
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
      finalize(() => {
        this.loadingSubject.next(false);
      }),
    );
  }

  exportToExcel(
    params: VesSwapReportQueryParams = {},
  ): Observable<Blob> {
    this.loadingSubject.next(true);

    let httpParams = new HttpParams();
    if (params.userId) httpParams = httpParams.set('userId', params.userId);
    if (params.startDate)
      httpParams = httpParams.set('startDate', params.startDate);
    if (params.endDate) httpParams = httpParams.set('endDate', params.endDate);

    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        return this.http.get(
          `${this.apiUrl}binance-deposits/ves-swaps/export`,
          {
            headers,
            params: httpParams,
            responseType: 'blob',
          },
        );
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
      finalize(() => {
        this.loadingSubject.next(false);
      }),
    );
  }

  downloadExcelFile(blob: Blob, filename: string = 'swaps-usdt-ves.xlsx') {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  }
}
